/**
 * HTML marker utilities for MapLibre GL JS.
 * Replaces L.marker + L.divIcon and L.layerGroup patterns.
 */

/**
 * Manages a collection of maplibregl.Marker instances as a group.
 * Replaces L.layerGroup([markers]).addTo(map).
 */
export class MarkerGroup {
    constructor() {
        this.markers = [];
        this._map = null;
    }

    add(marker) {
        this.markers.push(marker);
        if (this._map) marker.addTo(this._map);
        return this;
    }

    addTo(map) {
        this._map = map;
        for (var i = 0; i < this.markers.length; i++) {
            this.markers[i].addTo(map);
        }
        return this;
    }

    remove() {
        for (var i = 0; i < this.markers.length; i++) {
            this.markers[i].remove();
        }
        this.markers = [];
        this._map = null;
        return this;
    }

    clear() {
        return this.remove();
    }

    getMarkers() {
        return this.markers;
    }
}

/**
 * Manages a collection of maplibregl.Marker instances keyed by a stable id.
 * Unlike MarkerGroup it supports incremental diffing: markers already present
 * stay on the map across map moves, only new keys are added and stale keys
 * removed. Used by layers that accumulate data instead of replacing it.
 */
export class KeyedMarkerGroup {
    constructor() {
        this.markers = new Map(); // key -> maplibregl.Marker
        this._map = null;
    }

    addTo(map) {
        this._map = map;
        this.markers.forEach(function(marker) {
            marker.addTo(map);
        });
        return this;
    }

    has(key) {
        return this.markers.has(String(key));
    }

    get(key) {
        return this.markers.get(String(key));
    }

    set(key, marker) {
        key = String(key);
        var prev = this.markers.get(key);
        if (prev) {
            prev.remove();
        }
        this.markers.set(key, marker);
        if (this._map) {
            marker.addTo(this._map);
        }
        return this;
    }

    delete(key) {
        key = String(key);
        var marker = this.markers.get(key);
        if (marker) {
            marker.remove();
            this.markers.delete(key);
        }
        return this;
    }

    keys() {
        return Array.from(this.markers.keys());
    }

    size() {
        return this.markers.size;
    }

    getMarkers() {
        return Array.from(this.markers.values());
    }

    remove() {
        this.markers.forEach(function(marker) {
            marker.remove();
        });
        this.markers.clear();
        this._map = null;
        return this;
    }

    clear() {
        return this.remove();
    }
}

/**
 * Create a maplibregl.Marker with a custom HTML element.
 * Replaces L.marker(latlng, { icon: L.divIcon({ className, html, iconAnchor }) }).
 *
 * @param {Array} lngLat - [lng, lat]
 * @param {string} html - innerHTML for the marker element
 * @param {string} className - CSS class(es) for the wrapper div
 * @param {Object} [options] - Additional options
 * @param {Array} [options.offset] - [x, y] pixel offset
 * @param {string} [options.anchor] - MapLibre marker anchor
 * @param {Function} [options.onClick] - Click handler
 * @returns {maplibregl.Marker}
 */
export function createHtmlMarker(lngLat, html, className, options) {
    options = options || {};
    var el = document.createElement('div');
    el.className = className || '';
    el.innerHTML = html;

    if (options.onClick) {
        el.style.cursor = 'pointer';
        el.addEventListener('click', function(e) {
            e.stopPropagation();
            options.onClick(e);
        });
    }

    var markerOpts = { element: el, anchor: options.anchor || 'center' };
    if (options.offset) markerOpts.offset = options.offset;

    return new maplibregl.Marker(markerOpts).setLngLat(lngLat);
}
